// RelatorioHTML.js
// Utilitário simples para imprimir/em/exportar relatórios tabulares no CECWeb (Vue).
// Sem dependências externas.

import * as XLSX from 'xlsx';

function pad2(n){ return String(n).padStart(2,'0'); }
function fmtDate(d){
  if(!d) return '';
  const dt = (d instanceof Date) ? d : new Date(d);
  if(Number.isNaN(+dt)) return '';
  return `${pad2(dt.getDate())}/${pad2(dt.getMonth()+1)}/${dt.getFullYear()}`;
}
function fmtDateTime(d){
  if(!d) return '';
  const dt = (d instanceof Date) ? d : new Date(d);
  if(Number.isNaN(+dt)) return '';
  return `${fmtDate(dt)} ${pad2(dt.getHours())}:${pad2(dt.getMinutes())}`;
}

const TYPE_ALIGN = {
  text: 'left',
  number: 'right',
  currency: 'right',
  percent: 'right',
  date: 'center',
  datetime: 'center',
};

function defaultFormatter(type, value, locale, currency){
  if(value == null) return '';
  switch(type){
    case 'currency':
      return Number(value).toLocaleString(locale, { style:'currency', currency });
    case 'number':
      return Number(value).toLocaleString(locale);
    case 'percent': {
      const v = Number(value);
      // aceita 0–1 ou 0–100; detecta automaticamente
      const p = (v > 1 ? v : v*100);
      return `${p.toLocaleString(locale, { maximumFractionDigits: 2 })}%`;
    }
    case 'date':    return fmtDate(value);
    case 'datetime':return fmtDateTime(value);
    default:        return String(value);
  }
}

function normalizeColumns(cols, locale, currency){
  return (cols || []).map((c,i) => {
    const col = { key:c.key ?? c.field ?? `col${i}`, label:c.label ?? c.key ?? `Coluna ${i+1}`, ...c };
    col.type = col.type || 'text';
    col.align = col.align || TYPE_ALIGN[col.type] || 'left';
    col.formatter = col.formatter || ((val,row) => defaultFormatter(col.type, val, locale, currency));
    col.get = col.get || ((row) => row[col.key]);
    return col;
  });
}

function buildStyle({ pageSize='A4', landscape=false }){
  return `
  <style>
    :root{
      --fs-base: 12px;
      --c-border:#e5e7eb;
      --c-th:#f5f6f7;
      --c-head:#111827;
      --c-muted:#6b7280;
    }
    *{ box-sizing:border-box }
    body{ font: var(--fs-base) Arial, Helvetica, sans-serif; margin:16px; color:#111 }
    h1,h2,h3{ margin: 0 0 8px 0 }
    .head{
      display:grid; gap:6px;
      grid-template-columns: 1fr auto;
      border-bottom:1px solid var(--c-border);
      padding-bottom:8px; margin-bottom:12px;
    }
    .head .meta{ text-align:right; color:var(--c-muted); font-size:11px }
    .obs{ margin:8px 0 12px 0; white-space:pre-wrap; color:#374151 }
    table{ width:100%; border-collapse: collapse; table-layout: fixed }
    thead th{
      position: sticky; top:0; z-index:1;
      background: var(--c-th);
      color:#111; text-align:left; border:1px solid var(--c-border);
      padding:6px; font-weight:600;
    }
    tbody td{
      border:1px solid var(--c-border);
      padding:6px; vertical-align: top;
      word-wrap: break-word; overflow-wrap: anywhere;
    }
    tbody tr:nth-child(even){ background:#fafafa }
    tfoot td{
      border:1px solid var(--c-border);
      padding:6px; font-weight:700; background:#fcfcfc;
    }
    .left{text-align:left}.right{text-align:right}.center{text-align:center}
    .nowrap{ white-space:nowrap }
    .w-100{ width:100% }

    @media print {
      @page { size: ${landscape ? `${pageSize} landscape` : pageSize}; margin: 12mm }
      body{ margin:0 }
      a[href]:after{ content:"" }
    }
  </style>`;
}

function buildHeader({ systemName='CECWeb', reportTitle='Relatório', printedAt=new Date(), printedBy='—', observations='' }){
  const when = fmtDateTime(printedAt);
  return `
    <div class="head">
      <div>
        <div style="font-size:11px;font-weight:600">${systemName}</div>
        <div style="font-size:11px;font-weight:600">${reportTitle}</div>
      </div>
      <div class="meta">
        <div><strong>Impressão:</strong> ${when}</div>
        <div><strong>Usuário:</strong> ${printedBy}</div>
      </div>
    </div>
    ${observations ? `<div class="obs">${observations}</div>` : '' }
  `;
}

function buildTable({ columns, rows, locale, currency, footerTotals }){
  const thead = `
    <thead><tr>
      ${columns.map(c => `<th${c.width?` style="width:${c.width}"`:''} class="${c.nowrap?'nowrap':''} ${c.align}">${c.label}</th>`).join('')}
    </tr></thead>`;

  const tbody = `
    <tbody>
      ${rows.map(r => {
        const tds = columns.map(c => {
          const raw = c.get(r);
          const html = c.formatter ? c.formatter(raw, r) : raw;
          return `<td class="${c.nowrap?'nowrap':''} ${c.align}">${html ?? ''}</td>`;
        }).join('');
        return `<tr>${tds}</tr>`;
      }).join('')}
    </tbody>`;

  let tfoot = '';
  if (footerTotals && footerTotals.length){
    const mapTotals = new Map(footerTotals.map(t => [t.key, t]));
    const cells = columns.map(c => {
      const t = mapTotals.get(c.key);
      if(!t) return '<td></td>';
      const reducer = t.reducer || ((sum, row) => sum + (Number(c.get(row)) || 0));
      const value = rows.reduce(reducer, 0);
      const fmt = t.formatter || c.formatter || (v => v);
      return `<td class="${c.align}">${fmt(value)}</td>`;
    }).join('');
    tfoot = `<tfoot><tr>${cells}</tr></tfoot>`;
  }

  return `<table class="w-100">${thead}${tbody}${tfoot}</table>`;
}

function escapeHtml(value){
  if(value == null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function buildGroupedStyle({ pageSize='A4', landscape=false }){
  return `
  <style>
    :root{
      --fs-base: 10px;
      --c-border:#9ca3af;
      --c-soft:#f3f4f6;
      --c-muted:#4b5563;
      --c-head:#111827;
    }
    *{ box-sizing:border-box }
    body{ font: var(--fs-base) "Courier New", Courier, monospace; margin:10px; color:#111 }
    .report-head{
      display:grid;
      grid-template-columns: 1fr 2fr 1fr;
      gap:8px;
      align-items:start;
      border-bottom:1px dashed var(--c-border);
      padding-bottom:6px;
      margin-bottom:8px;
    }
    .report-head .center{ text-align:center }
    .report-head .right{ text-align:right }
    .title{ font-weight:700; text-transform:uppercase }
    .filters{
      white-space:pre-wrap;
      border-bottom:1px dashed var(--c-border);
      padding-bottom:6px;
      margin-bottom:8px;
      color:var(--c-muted);
    }
    table{ width:100%; border-collapse:collapse; table-layout:fixed }
    th{
      border-bottom:1px dashed var(--c-border);
      padding:4px 3px;
      text-align:left;
      font-weight:700;
    }
    td{ padding:3px; vertical-align:top; overflow-wrap:anywhere }
    .left{text-align:left}.right{text-align:right}.center{text-align:center}
    .nowrap{ white-space:nowrap }
    .group-row td{
      border-top:1px dashed var(--c-border);
      padding-top:8px;
      font-weight:700;
      background:#fff;
    }
    .subtotal-row td{
      border-top:1px dashed var(--c-border);
      border-bottom:1px dashed var(--c-border);
      font-weight:700;
      background:#fafafa;
    }
    .total-row td{
      border-top:2px solid #111;
      font-weight:700;
      background:var(--c-soft);
    }
    @media print {
      @page { size: ${landscape ? `${pageSize} landscape` : pageSize}; margin: 8mm }
      body{ margin:0 }
      .no-print{ display:none }
    }
  </style>`;
}

function buildGroupedHeader({ systemName='', reportTitle='Relatorio', printedAt=new Date(), printedBy='', pageHint='', observations='' }){
  const when = fmtDateTime(printedAt);
  return `
    <div class="report-head">
      <div>
        <div>Data: ${escapeHtml(fmtDate(printedAt))}</div>
        <div>Hora: ${escapeHtml(`${pad2(printedAt.getHours())}:${pad2(printedAt.getMinutes())}`)}</div>
      </div>
      <div class="center">
        <div class="title">${escapeHtml(systemName)}</div>
        <div>${escapeHtml(reportTitle)}</div>
        ${printedBy ? `<div>Usuario: ${escapeHtml(printedBy)}</div>` : ''}
      </div>
      <div class="right">${escapeHtml(pageHint)}</div>
    </div>
    ${observations ? `<div class="filters">${escapeHtml(observations)}</div>` : '' }
    <div style="text-align:center; margin-bottom:8px">Emitido em ${escapeHtml(when)}</div>
  `;
}

function sumRows(rows, key){
  return rows.reduce((sum, row) => sum + (Number(row[key]) || 0), 0);
}

function summaryValue(col, rows){
  if(typeof col.summary === 'function'){
    return col.summary(rows, col);
  }
  return sumRows(rows, col.key);
}

function formatCell(col, value, row){
  const html = col.formatter ? col.formatter(value, row) : value;
  return escapeHtml(html ?? '');
}

function buildSummaryCells({ columns, rows, sumKeys, label }){
  const keys = new Set(sumKeys || []);
  const firstSummaryIndex = columns.findIndex((col) => keys.has(col.key));

  if(firstSummaryIndex === -1){
    return `<td colspan="${columns.length}" class="left">${escapeHtml(label)}</td>`;
  }

  const cells = [];
  let summaryStartIndex = firstSummaryIndex;

  if(firstSummaryIndex === 0){
    const labelSpan = Math.min(3, columns.length);
    const inlineSummaries = columns
      .slice(0, labelSpan)
      .filter((col) => keys.has(col.key))
      .map((col) => {
        const value = summaryValue(col, rows);
        return `${col.label}: ${col.formatter ? col.formatter(value, rows[0] || {}) : value}`;
      });

    const labelText = [label, ...inlineSummaries].filter(Boolean).join(' | ');
    cells.push(`<td colspan="${labelSpan}" class="left">${escapeHtml(labelText)}</td>`);
    summaryStartIndex = labelSpan;
  }

  if(summaryStartIndex > 0){
    if(firstSummaryIndex !== 0){
      cells.push(`<td colspan="${summaryStartIndex}" class="left">${escapeHtml(label)}</td>`);
    }
  }

  columns.slice(summaryStartIndex).forEach((col) => {
    if(keys.has(col.key)){
      const value = summaryValue(col, rows);
      cells.push(`<td class="${col.align}">${formatCell(col, value, rows[0] || {})}</td>`);
      return;
    }
    cells.push('<td></td>');
  });

  return cells.join('');
}

function buildPureSummaryCells({ columns, rows, sumKeys }){
  const keys = new Set(sumKeys || []);
  return columns.map((col) => {
    if(keys.has(col.key)){
      const value = summaryValue(col, rows);
      return `<td class="${col.align}">${formatCell(col, value, rows[0] || {})}</td>`;
    }
    return '<td></td>';
  }).join('');
}

function normalizeGroupConfigs(groupBy){
  if(!groupBy) return [];
  return Array.isArray(groupBy) ? groupBy.filter(Boolean) : [groupBy];
}

function getGroupKey(groupConfig, row){
  if(groupConfig.getKey) return groupConfig.getKey(row);
  if(groupConfig.key) return row[groupConfig.key];
  return 'GERAL';
}

function getGroupLabel(groupConfig, row){
  if(groupConfig.getLabel) return groupConfig.getLabel(row);
  if(!groupConfig.key) return 'Geral';
  const value = row[groupConfig.key];
  return groupConfig.formatter ? groupConfig.formatter(value, row) : value;
}

function buildSummaryRows({ columns, rows, sumKeys, label, rowClass }){
  const keys = new Set(sumKeys || []);
  const firstSummaryIndex = columns.findIndex((col) => keys.has(col.key));

  if(firstSummaryIndex !== 0){
    return `<tr class="${rowClass}">${buildSummaryCells({ columns, rows, sumKeys, label })}</tr>`;
  }

  return `
    <tr class="${rowClass}"><td colspan="${columns.length}" class="left">${escapeHtml(label)}</td></tr>
    <tr class="${rowClass}">${buildPureSummaryCells({ columns, rows, sumKeys })}</tr>
  `;
}

function buildNestedGroupedRows({ columns, rows, groups, level, sumKeys, buildSubtotalLabel }){
  if(level >= groups.length){
    return rows.map((row) => {
      const cells = columns.map(col => {
        const raw = col.get(row);
        return `<td class="${col.nowrap?'nowrap':''} ${col.align}">${formatCell(col, raw, row)}</td>`;
      }).join('');
      return `<tr>${cells}</tr>`;
    }).join('');
  }

  const groupConfig = groups[level];
  const buckets = [];
  const bucketIndex = new Map();

  rows.forEach((row) => {
    const key = getGroupKey(groupConfig, row) ?? `GERAL_${level}`;
    if(!bucketIndex.has(key)){
      const bucket = {
        key,
        label: getGroupLabel(groupConfig, row) || 'Geral',
        rows: []
      };
      bucketIndex.set(key, bucket);
      buckets.push(bucket);
    }
    bucketIndex.get(key).rows.push(row);
  });

  return buckets.map((bucket) => {
    const detail = buildNestedGroupedRows({
      columns,
      rows: bucket.rows,
      groups,
      level: level + 1,
      sumKeys,
      buildSubtotalLabel
    });

    const subtotalLabel = buildSubtotalLabel(bucket, groupConfig, level);
    const subtotal = buildSummaryRows({
      columns,
      rows: bucket.rows,
      sumKeys,
      label: subtotalLabel,
      rowClass: 'subtotal-row'
    });

    return `
      <tr class="group-row group-level-${level + 1}">
        <td colspan="${columns.length}">${escapeHtml(bucket.label)}</td>
      </tr>
      ${detail}
      ${subtotal}
    `;
  }).join('');
}

function buildGroupedTable({ columns, rows, groupBy, sumKeys, footerLabel = 'Total Geral:' }){
  const thead = `
    <thead><tr>
      ${columns.map(c => `<th${c.width?` style="width:${c.width}"`:''} class="${c.nowrap?'nowrap':''} ${c.align}">${escapeHtml(c.label)}</th>`).join('')}
    </tr></thead>`;

  const groups = normalizeGroupConfigs(groupBy);
  const buildSubtotalLabel = (bucket, groupConfig, level) => {
    const baseLabel = groupConfig?.subtotalLabel || `Total do Grupo: ${bucket.label}`;
    return `${baseLabel} (${bucket.rows.length} registros)`;
  };

  const bodyRows = groups.length
    ? buildNestedGroupedRows({ columns, rows, groups, level: 0, sumKeys, buildSubtotalLabel })
    : buildNestedGroupedRows({ columns, rows, groups: [], level: 0, sumKeys, buildSubtotalLabel });

  const total = buildSummaryRows({
    columns,
    rows,
    sumKeys,
    label: `${footerLabel} (${rows.length} registros)`,
    rowClass: 'total-row'
  });

  return `<table>${thead}<tbody>${bodyRows}${total}</tbody></table>`;
}

function buildSimpleTable({ title = '', columns, rows }){
  return `
    <div style="margin-top:16px">
      ${title ? `<div class="title" style="text-align:left; margin-bottom:6px">${escapeHtml(title)}</div>` : ''}
      ${buildGroupedTable({ columns, rows, groupBy: null, sumKeys: columns.filter(col => col.summary !== false && ['number', 'currency', 'percent'].includes(col.type)).map(col => col.key), footerLabel: 'Total Geral:' })}
    </div>
  `;
}

// --------- API pública ---------

/**
 * Abre uma nova aba com o HTML e (opcionalmente) chama window.print().
 */
export function printHtmlReport({
  rows = [],
  columns = [],
  systemName = `${window.localStorage.getItem('nomeempresa') || ''}`.trim(),
  reportTitle = 'Relatório',
  printedBy = '—',
  printedAt = new Date(),
  observations = '',
  locale = 'pt-BR',
  currency = 'BRL',
  pageSize = 'A4',
  landscape = false,
  footerTotals = null, // [{ key, reducer?, formatter? }]
  autoPrint = true,
} = {}){
  const cols = normalizeColumns(columns, locale, currency);
  const html = `
  <!doctype html><html><head>
    <meta charset="utf-8">
    <title>CECWEB: ${reportTitle}</title>
    ${buildStyle({ pageSize, landscape })}
  </head><body>
    ${buildHeader({ systemName, reportTitle, printedAt, printedBy, observations })}
    ${buildTable({ columns: cols, rows, locale, currency, footerTotals })}
  </body></html>`.trim();

  const w = window.open('', '_blank');
  w.document.open('text/html', 'replace');
  w.document.write(html);
  w.document.close();
  w.focus();
  if (autoPrint) w.print();
}

export function printGroupedHtmlReport({
  rows = [],
  columns = [],
  groupBy = null,
  sumKeys = [],
  systemName = `${window.localStorage.getItem('nomeempresa') || ''}`.trim(),
  reportTitle = 'Relatorio',
  printedBy = '',
  printedAt = new Date(),
  observations = '',
  pageSize = 'A4',
  landscape = false,
  locale = 'pt-BR',
  currency = 'BRL',
  autoPrint = true,
  footerLabel = 'Total Geral:',
  extraSections = [],
} = {}){
  const cols = normalizeColumns(columns, locale, currency);
  const normalizedSections = (extraSections || []).map((section) => ({
    title: section.title || '',
    rows: section.rows || [],
    columns: normalizeColumns(section.columns || [], locale, currency)
  }));
  const html = `
  <!doctype html><html><head>
    <meta charset="utf-8">
    <title>CECWEB: ${escapeHtml(reportTitle)}</title>
    ${buildGroupedStyle({ pageSize, landscape })}
  </head><body>
    ${buildGroupedHeader({ systemName, reportTitle, printedAt, printedBy, pageHint:'Pg.', observations })}
    ${buildGroupedTable({ columns: cols, rows, groupBy, sumKeys, footerLabel })}
    ${normalizedSections.map(section => buildSimpleTable({ title: section.title, columns: section.columns, rows: section.rows })).join('')}
  </body></html>`.trim();

  const w = window.open('', '_blank');
  w.document.open('text/html', 'replace');
  w.document.write(html);
  w.document.close();
  w.focus();
  if (autoPrint) w.print();
}

/**
 * Exporta em "Excel" (HTML de tabela com MIME do Excel).
 * Abre o diálogo de download com .xls (Excel abrirá normalmente).
 */
// coloca no mesmo RelatorioHTML.js
// substitua sua função atual por esta
export function exportExcelXlsx({
  rows = [],
  columns = [],
  filename = 'relatorio.xlsx',
  sumKey = null,        // ex.: 'DEBITO' para somar no rodapé
  sheetName = 'Relatorio'
} = {}) {
  const cols = (columns || []).map((c, i) => ({
    key:   c.key ?? c.field ?? `col${i}`,
    label: c.label ?? c.key ?? `Coluna ${i+1}`,
    type:  c.type || 'text', // text|number|currency|percent|date|datetime
    get:   c.get  || (r => r[c.key]),
  }));

  // cabeçalho + dados "crus"
  const aoa = [
    cols.map(c => c.label),
    ...rows.map(r => cols.map(c => c.get(r)))
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');

  // --- FORMATOS POR TIPO (datas e moeda de verdade) ---
  for (let C = range.s.c; C <= range.e.c; C++) {
    const col = cols[C];
    for (let R = range.s.r + 1; R <= range.e.r; R++) {
      const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
      const raw = rows[R - 1]?.[col.key];
      let cell = ws[cellRef];
      if (!cell) cell = ws[cellRef] = { v: null };

      switch (col.type) {
        case 'number': {
          const v = Number(raw);
          cell.t = 'n'; cell.v = isFinite(v) ? v : null;
          break;
        }
        case 'currency': {
          const v = Number(raw);
          cell.t = 'n'; cell.v = isFinite(v) ? v : null;
          cell.z = '"R$" #,##0.00';
          break;
        }
        case 'percent': {
          let v = Number(raw);
          if (isFinite(v)) { if (v > 1) v = v / 100; } else v = null;
          cell.t = 'n'; cell.v = v; cell.z = '0.00%';
          break;
        }
        case 'date':
        case 'datetime': {
          // ✅ data real: use tipo 'd' + Date nativo (sem UTC manual)
          const dt = raw ? new Date(raw) : null;
          if (dt && !isNaN(+dt)) {
            cell.t = 'd';
            cell.v = dt; // SheetJS converte p/ serial corretamente
            cell.z = col.type === 'date' ? 'dd/mm/yyyy' : 'dd/mm/yyyy hh:mm';
          } else {
            cell.t = 's'; cell.v = raw == null ? '' : String(raw);
          }
          break;
        }
        default: {
          cell.t = 's'; cell.v = raw == null ? '' : String(raw);
        }
      }
    }
  }

  // larguras
  ws['!cols'] = cols.map(c => ({ wch: Math.max(12, String(c.label).length + 2) }));

  // 🔎 Autofiltro no cabeçalho
  ws['!autofilter'] = { ref: ws['!ref'] };

  // ➕ Linha de TOTAL (opcional)
  if (sumKey) {
    const sumColIndex = cols.findIndex(c => c.key === sumKey);
    if (sumColIndex >= 0) {
      const lastRow = range.e.r + 1;           // próxima linha
      const A1 = r => XLSX.utils.encode_cell({ r, c: 0 });
      const sumCellRef = XLSX.utils.encode_cell({ r: lastRow, c: sumColIndex });

      // "TOTAL" na primeira coluna
      ws[A1(lastRow)] = { t: 's', v: 'TOTAL' };

      // fórmula de soma na coluna escolhida (da linha 2 até a última)
      const colLetter = XLSX.utils.encode_col(sumColIndex);
      const from = 2;
      const to = range.e.r + 1;
      ws[sumCellRef] = { t: 'n', f: `SUM(${colLetter}${from}:${colLetter}${to})` };
      ws[sumCellRef].z = '"R$" #,##0.00';

      // atualiza ref da planilha
      ws['!ref'] = XLSX.utils.encode_range({
        s: range.s,
        e: { r: lastRow, c: range.e.c }
      });
    }
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, filename);
}



/**
 * Exporta CSV (separador ; para PT-BR).
 */
export function exportCsv({
  rows = [],
  columns = [],
  filename = 'relatorio.csv',
  separator = ';',
  locale = 'pt-BR',
  currency = 'BRL',
} = {}){
  const cols = normalizeColumns(columns, locale, currency);
  const head = cols.map(c => `"${(c.label||'').replace(/"/g,'""')}"`).join(separator);
  const lines = rows.map(r =>
    cols.map(c => {
      const v = c.formatter ? c.formatter(c.get(r), r) : c.get(r);
      const s = (v==null?'':String(v)).replace(/"/g,'""');
      return `"${s}"`;
    }).join(separator)
  );
  const csv = [head, ...lines].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(a.href);
  a.remove();
}
/**
 * Método de conveniência para imprimir HTML bruto (usado em espelhos e layouts customizados).
 */
export function imprimir(html, titulo = 'Relatório', tamanho = 'A4', orientacao = 'portrait') {
  const w = window.open('', '_blank');
  w.document.open('text/html', 'replace');
  
  // Se o HTML já for um documento completo, usa ele. Caso contrário, envolve em um básico.
  let fullHtml = html;
  if (!html.includes('<html')) {
    const landscape = orientacao === 'landscape';
    fullHtml = `
      <!doctype html><html><head>
        <meta charset="utf-8">
        <title>${titulo}</title>
        ${buildStyle({ pageSize: tamanho, landscape })}
      </head><body>
        <div class="head">
          <div style="font-size:11px;font-weight:600">${window.localStorage.getItem('nomeempresa') || 'CECWeb'}</div>
          <div style="font-size:11px;font-weight:600">${titulo}</div>
          <div style="font-size:10px;text-align:right">Impressão: ${fmtDateTime(new Date())}</div>
        </div>
        ${html}
        <style>
          .grid { width: 100%; border-collapse: collapse; margin-top: 10px; }
          .grid th, .grid td { border: 1px solid #ddd; padding: 6px; font-size: 11px; }
          .grid th { background: #f5f5f5; text-align: left; }
        </style>
      </body></html>
    `.trim();
  }

  w.document.write(fullHtml);
  w.document.close();
  w.focus();
  w.print();
}

export default {
  printHtmlReport,
  exportExcelXlsx,
  exportCsv,
  imprimir
}



// WEBPACK FOOTER //
// ./src/utils/RelatorioHTML.js